// AudioSocketStreamer
// Captures audio from a provided MediaStream, converts to PCM16 (mono),
// optionally downsamples to a target sample rate, and sends chunks over a socket.
// Supports Socket.IO (emit) and raw WebSocket (send) with configurable event names.

export default class AudioSocketStreamer {
  constructor({
    mediaStream,
    socket,
    options = {},
  }) {
    if (!mediaStream) {
      throw new Error('AudioSocketStreamer: mediaStream is required');
    }
    if (!socket) {
      throw new Error('AudioSocketStreamer: socket is required');
    }

    this.mediaStream = mediaStream;
    this.socket = socket.getSocket();
    this.options = {
      // 'socketio' | 'websocket' - auto-detect if not set
      socketType: this._detectSocketType(socket),
      // Event name for sending audio when using socket.io
      sendEventName: 'stt-audio',
      // Event name to listen for STT results (socket.io)
      resultEventName: 'stt-result',
      // Transport for payload: 'binary' | 'base64' | 'string'
      transport: 'binary',
      // Target sample rate for STT backends (e.g., 16000)
      targetSampleRate: 16000,
      // Size of ScriptProcessor buffer
      bufferSize: 4096,
      // Milliseconds to batch before sending (0 = send every callback)
      sendIntervalMs: 0,
      // Optional: attach a callback for results
      onResult: null,
      // Optional: attach a callback for errors
      onError: null,
      // Optional: attach a callback for debug logs
      onDebug: null,
      // Whether to start immediately
      autoStart: true,
      // Whether to mute the captured audio to avoid echo (no output node)
      muteCapture: true,
      // Whether to normalize input amplitude before conversion
      normalize: true,
      // Normalization gain target (peak)
      normalizeTarget: 0.98,
      // Optional metadata sent with each chunk (e.g., language)
      metadata: null,
      ...options,
    };

    this.audioContext = null;
    this.sourceNode = null;
    this.processorNode = null;
    this.workletNode = null;
    this.gainNode = null;
    this._chunkFloat32Buffer = [];
    this._chunkTimer = null;
    this._isStreaming = false;
    this._boundOnResult = null;

    if (this.options.autoStart) {
      this.start().catch((err) => this._emitError(err));
    }
  }

  updateOptions(next) {
    this.options = { ...this.options, ...(next || {}) };
  }

  _detectSocketType(socket) {
    // Heuristic: Socket.IO client has 'emit' and not 'send' for binary frames typically
    // Raw WebSocket has 'send' and readyState
    if (socket && typeof socket.emit === 'function' && typeof socket.on === 'function') {
      return 'socketio';
    }
    if (socket && typeof socket.send === 'function' && typeof socket.readyState !== 'undefined') {
      return 'websocket';
    }
    return 'socketio';
  }

  async start() {
    if (this._isStreaming) return;
    // Ensure there is at least one audio track; if not, try to proceed but will fail to create source
    const hasAudio = this.mediaStream.getAudioTracks().length > 0;
    if (!hasAudio) {
      throw new Error('AudioSocketStreamer: provided MediaStream has no audio tracks');
    }

    this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream);

    // Optional input gain/normalization stage
    this.gainNode = this.audioContext.createGain();
    this.gainNode.gain.value = 1.0;

    const inputSampleRate = this.audioContext.sampleRate;
    const targetRate = this.options.targetSampleRate || inputSampleRate;
    const normalize = !!this.options.normalize;
    const normalizeTarget = this.options.normalizeTarget;

    // Prefer AudioWorkletNode, fallback to ScriptProcessorNode
    const canUseWorklet = !!this.audioContext.audioWorklet && typeof AudioWorkletNode !== 'undefined';
    this.sourceNode.connect(this.gainNode);
    if (canUseWorklet) {
      await this._initAudioWorkletNode();
      this.gainNode.connect(this.workletNode);
      this.workletNode.port.onmessage = (event) => {
        try {
          const input = event.data; // Float32Array mono
          let floatData = input;
          if (normalize) {
            const max = this._absMax(input);
            const gain = max > 0 ? Math.min(normalizeTarget / max, 10) : 1.0;
            if (gain !== 1.0) {
              floatData = this._applyGain(input, gain);
            }
          }
          let mono16;
          if (inputSampleRate !== targetRate) {
            const downsampled = this._downsampleBuffer(floatData, inputSampleRate, targetRate);
            mono16 = this._convertFloat32ToPCM16(downsampled);
          } else {
            mono16 = this._convertFloat32ToPCM16(floatData);
          }
          this._enqueueChunk(mono16);
        } catch (err) {
          this._emitError(err);
        }
      };
      // No connection to destination to keep capture muted
    } else {
      // ScriptProcessorNode (deprecated) fallback
      const bufferSize = this.options.bufferSize;
      const inputChannels = 1;
      const outputChannels = 1;
      // Access via bracket notation to avoid TS deprecation warnings; used only as a fallback
      this.processorNode = this.audioContext['createScriptProcessor'](bufferSize, inputChannels, outputChannels);
      this.gainNode.connect(this.processorNode);
      if (!this.options.muteCapture) {
        this.processorNode.connect(this.audioContext.destination);
      }
      this.processorNode.onaudioprocess = (e) => {
        try {
          const input = e.inputBuffer.getChannelData(0);
          let floatData = input;
          if (normalize) {
            const max = this._absMax(input);
            const gain = max > 0 ? Math.min(normalizeTarget / max, 10) : 1.0;
            if (gain !== 1.0) {
              floatData = this._applyGain(input, gain);
            }
          }
          let mono16;
          if (inputSampleRate !== targetRate) {
            const downsampled = this._downsampleBuffer(floatData, inputSampleRate, targetRate);
            mono16 = this._convertFloat32ToPCM16(downsampled);
          } else {
            mono16 = this._convertFloat32ToPCM16(floatData);
          }
          this._enqueueChunk(mono16);
        } catch (err) {
          this._emitError(err);
        }
      };
    }

    // Setup result listener if requested and using socket.io
    if (this.options.resultEventName && this.options.socketType === 'socketio') {
      this._boundOnResult = (payload) => {
        if (typeof this.options.onResult === 'function') {
          this.options.onResult(payload);
        }
      };
      this.socket.on(this.options.resultEventName, this._boundOnResult);
    }

    // Start sending timer if batching
    if (this.options.sendIntervalMs > 0) {
      this._chunkTimer = setInterval(() => {
        this._flushChunks();
      }, this.options.sendIntervalMs);
    }

    this._isStreaming = true;
    this._debug(`AudioSocketStreamer started (ctxRate=${inputSampleRate}, targetRate=${targetRate})`);
  }

  stop() {
    if (!this._isStreaming) return;
    try {
      if (this._chunkTimer) {
        clearInterval(this._chunkTimer);
        this._chunkTimer = null;
      }
      this._flushChunks(); // flush remaining

      if (this.processorNode) {
        this.processorNode.disconnect();
        this.processorNode.onaudioprocess = null;
      }
      if (this.workletNode) {
        try {
          this.workletNode.port.onmessage = null;
        } catch (e) { }
        this.workletNode.disconnect();
      }
      if (this.gainNode) {
        this.gainNode.disconnect();
      }
      if (this.sourceNode) {
        this.sourceNode.disconnect();
      }
      // Do not close the shared AudioContext abruptly if others may use it
      if (this.audioContext && this.audioContext.state !== 'closed') {
        this.audioContext.close().catch(() => { });
      }

      if (this._boundOnResult && this.options.socketType === 'socketio' && this.options.resultEventName) {
        this.socket.off?.(this.options.resultEventName, this._boundOnResult);
      }
    } catch (err) {
      this._emitError(err);
    } finally {
      this.audioContext = null;
      this.sourceNode = null;
      this.processorNode = null;
      this.workletNode = null;
      this.gainNode = null;
      this._chunkFloat32Buffer = [];
      this._boundOnResult = null;
      this._isStreaming = false;
      this._debug('AudioSocketStreamer stopped');
    }
  }

  async _initAudioWorkletNode() {
    // Inline module to avoid asset path issues
    const workletSource = `
      class STTCaptureProcessor extends AudioWorkletProcessor {
        process(inputs) {
          const input = inputs[0];
          if (input && input[0] && input[0].length) {
            const ch = input[0];
            const copy = new Float32Array(ch.length);
            copy.set(ch);
            this.port.postMessage(copy, [copy.buffer]);
          }
          return true;
        }
      }
      registerProcessor('stt-capture-processor', STTCaptureProcessor);
    `;
    const blob = new Blob([workletSource], { type: 'application/javascript' });
    const url = URL.createObjectURL(blob);
    try {
      await this.audioContext.audioWorklet.addModule(url);
      this.workletNode = new AudioWorkletNode(this.audioContext, 'stt-capture-processor', {
        numberOfInputs: 1,
        numberOfOutputs: 0,
        channelCount: 1,
      });
    } finally {
      // Release the blob URL; the module remains loaded
      URL.revokeObjectURL(url);
    }
  }

  destroy() {
    this.stop();
    // No further resources retained
  }

  isStreaming() {
    return this._isStreaming;
  }

  _enqueueChunk(int16Array) {
    // Buffer floats or int16 directly; we assembled PCM16 for transport, so push Int16Array
    this._chunkFloat32Buffer.push(int16Array);
    if (this.options.sendIntervalMs === 0) {
      this._flushChunks();
    }
  }

  _flushChunks() {
    if (this._chunkFloat32Buffer.length === 0) return;
    // Concatenate Int16 chunks
    const totalLength = this._chunkFloat32Buffer.reduce((acc, arr) => acc + arr.length, 0);
    const merged = new Int16Array(totalLength);
    let offset = 0;
    for (let i = 0; i < this._chunkFloat32Buffer.length; i++) {
      merged.set(this._chunkFloat32Buffer[i], offset);
      offset += this._chunkFloat32Buffer[i].length;
    }
    this._chunkFloat32Buffer = [];

    // Prepare payload per transport
    const transport = this.options.transport;
    let payload;
    let isBinary = false;
    if (transport === 'binary') {
      payload = merged.buffer; // ArrayBuffer
      isBinary = true;
    } else if (transport === 'base64') {
      payload = this._arrayBufferToBase64(merged.buffer);
    } else {
      // default string hex
      payload = this._int16ToHexString(merged);
    }

    const meta = {
      codec: 'pcm_s16le',
      channels: 1,
      sampleRate: this.options.targetSampleRate || (this.audioContext ? this.audioContext.sampleRate : 44100),
      timestamp: Date.now(),
      ...(this.options.metadata || {}),
    };

    try {
      if (this.options.socketType === 'socketio') {
        // Use event-based send
        this.socket.emit(this.options.sendEventName, { audio: payload, binary: isBinary, meta });
      } else {
        // Raw WebSocket: frame payload as JSON or binary
        if (isBinary) {
          this.socket.send(payload);
        } else {
          this.socket.send(JSON.stringify({ type: 'stt-audio', audio: payload, meta }));
        }
      }
    } catch (err) {
      this._emitError(err);
    }
  }

  _downsampleBuffer(buffer, sampleRate, outSampleRate) {
    if (outSampleRate === sampleRate) {
      return buffer;
    }
    if (outSampleRate > sampleRate) {
      // do not upsample, just return
      return buffer;
    }
    const sampleRateRatio = sampleRate / outSampleRate;
    const newLength = Math.round(buffer.length / sampleRateRatio);
    const result = new Float32Array(newLength);
    let offsetResult = 0;
    let offsetBuffer = 0;
    while (offsetResult < result.length) {
      const nextOffsetBuffer = Math.round((offsetResult + 1) * sampleRateRatio);
      // Average the samples between current and next
      let accum = 0;
      let count = 0;
      for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i++) {
        accum += buffer[i];
        count++;
      }
      result[offsetResult] = count > 0 ? accum / count : 0;
      offsetResult++;
      offsetBuffer = nextOffsetBuffer;
    }
    return result;
  }

  _convertFloat32ToPCM16(float32Array) {
    const len = float32Array.length;
    const pcm16 = new Int16Array(len);
    for (let i = 0; i < len; i++) {
      const s = Math.max(-1, Math.min(1, float32Array[i]));
      pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return pcm16;
  }

  _absMax(buffer) {
    let max = 0;
    for (let i = 0; i < buffer.length; i++) {
      const v = Math.abs(buffer[i]);
      if (v > max) max = v;
    }
    return max;
  }

  _applyGain(buffer, gain) {
    const out = new Float32Array(buffer.length);
    for (let i = 0; i < buffer.length; i++) {
      out[i] = buffer[i] * gain;
    }
    return out;
  }

  _arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  _int16ToHexString(int16Array) {
    let hex = '';
    for (let i = 0; i < int16Array.length; i++) {
      const val = (int16Array[i] & 0xffff).toString(16).padStart(4, '0');
      hex += val;
    }
    return hex;
  }

  _emitError(err) {
    if (typeof this.options.onError === 'function') {
      this.options.onError(err);
    } else {
      // eslint-disable-next-line no-console
      console.error('AudioSocketStreamer error:', err);
    }
  }

  _debug(msg) {
    if (typeof this.options.onDebug === 'function') {
      this.options.onDebug(msg);
    }
  }
}


